import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { QualityScorecard } from "@/components/QualityScorecard";
import { toast } from "sonner";
import { ShieldCheck, Upload, RotateCw } from "lucide-react";

export default function RiskRegisterQuality() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [raw, setRaw] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchTenant = useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get("/risk-register-quality");
      setData(r);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load risk register quality score");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTenant(); }, [fetchTenant]);

  const submitUpload = async () => {
    setSubmitting(true);
    try {
      let risks;
      try { const j = JSON.parse(raw); risks = Array.isArray(j) ? j : j.risks; }
      catch { toast.error("Upload must be a JSON array or {risks:[...]}"); setSubmitting(false); return; }
      if (!Array.isArray(risks) || risks.length === 0) { toast.error("Empty register"); setSubmitting(false); return; }
      const { data: r } = await api.post("/risk-register-quality/upload", { risks });
      setData(r);
      setUploadOpen(false);
      toast.success(`Scored ${risks.length} risks — Level ${r.scorecard.overall_level}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload scoring failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-violet-600 font-semibold flex items-center gap-2">
              <ShieldCheck size={14} /> EKSELENS · ERIO · Quality surfaces
            </p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-ek-ink">Risk Register Quality Score</h1>
            <p className="mt-1 text-sm text-ek-ink/65">Six rule-based checks scoring your live risk register against the EKSELENS PMO360 baseline. Charter-anchored remediation hints below.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchTenant} disabled={loading} className="flex items-center gap-2 rounded-md border border-black/10 bg-white px-3 py-2 text-sm font-medium text-ek-ink hover:border-ek-purple/40 disabled:opacity-50" data-testid="rrqs-refresh-btn">
              <RotateCw size={14} /> {loading ? "Loading…" : "Refresh"}
            </button>
            <button onClick={() => setUploadOpen((v) => !v)} className="flex items-center gap-2 rounded-md bg-ek-purple px-3 py-2 text-sm font-semibold text-white hover:bg-ek-purple/90" data-testid="rrqs-upload-toggle">
              <Upload size={14} /> Score an upload
            </button>
          </div>
        </div>

        {uploadOpen && (
          <div className="mt-6 rounded-xl bg-white p-5 ring-1 ring-slate-200" data-testid="rrqs-upload-panel">
            <h3 className="font-semibold text-ek-ink">Score an external register</h3>
            <p className="text-xs text-ek-ink/65 mb-2">Paste a JSON array of risk objects. Fields recognised: <code>risk_id, title, owner_user_id, inherent_score, residual_score, linked_signal_ids, supplier_id, controls, theme_id, updated_at, status</code>.</p>
            <textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={8} className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-800 focus:border-ek-purple focus:outline-none" data-testid="rrqs-upload-textarea" />
            <div className="mt-3 flex items-center gap-2">
              <button onClick={submitUpload} disabled={submitting} className="rounded-md bg-ek-purple px-4 py-2 text-sm font-semibold text-white hover:bg-ek-purple/90 disabled:opacity-50" data-testid="rrqs-upload-submit">
                {submitting ? "Scoring…" : "Score upload"}
              </button>
              <button onClick={() => setUploadOpen(false)} className="rounded-md border border-slate-200 px-4 py-2 text-sm text-ek-ink/70 hover:bg-slate-50">Cancel</button>
            </div>
          </div>
        )}

        <div className="mt-6">
          {loading ? (
            <div className="rounded-xl bg-white p-12 text-center text-slate-500 ring-1 ring-slate-200" data-testid="rrqs-loading">Loading scorecard…</div>
          ) : data ? (
            <QualityScorecard data={data} subjectLabel="risk register" testidPrefix="rrqs" />
          ) : (
            <div className="rounded-xl bg-white p-12 text-center text-slate-500 ring-1 ring-slate-200">No data yet.</div>
          )}
        </div>
      </main>
    </div>
  );
}
